import { useAtom, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import { noopSliderTransform, Slider } from "../ui/Slider"
import { useEditor } from "@/utils/useEditor"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { Icon } from "../icons/Icon"
import { Button } from "../ui/Button"
import { cropAspectLockAtom, cropStraightenGridAtom } from "@/components/Crop/crop-ui-state"
import { applyCropRatio, AspectRatioMenu } from "@/components/Crop/AspectRatioMenu"
import { photosViewAtom } from "@/store/photo/photos-view-state"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import {
	constrainCrop,
	handleCropFlip,
	handleCropRotate90,
	resetCropWithConstraint,
} from "@/components/Crop/crop-interaction-handlers"
import { getOrExtractImageInfoWithLock } from "@/store/processing/image-info"
import { CropRatio } from "@/core/types"
import { showingCropPanelAtom } from "@/store/ui-views-state"
import { ToolPanelProps } from "@/components/ControlsToolBar"

export const CropPanel = ({ adjustments, disabled }: ToolPanelProps) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const [previewState] = useAtom(photosViewAtom)
	const [aspectLock, setAspectLock] = useAtom(cropAspectLockAtom)
	const showStraightenGrid = useSetAtom(cropStraightenGridAtom)
	const setIsExpanded = useSetAtom(showingCropPanelAtom)

	const isDisabled = disabled || previewState !== "focus"

	const handleStraighten = (cropStraighten: number) => {
		showStraightenGrid(true)
		straighten(cropStraighten)
	}

	const handleStraightenEnd = (cropStraighten: number) => {
		showStraightenGrid(false)
		straighten(cropStraighten)
		saveCurrentAdjustmentState({
			message: "Crop Straighten",
			changedValue: cropStraighten.toFixed(2),
		})
	}

	const handleFlipAspect = () => {
		const [cropWidth, cropHeight] = editor.croppedImageSize

		const crop = applyCropRatio(editor.adjustments.crop, cropWidth / cropHeight, true)
		const cropRatio: CropRatio = [
			editor.adjustments.cropRatio[1],
			editor.adjustments.cropRatio[0],
		]

		const newCrop = constrainCrop(
			{ ...editor.adjustments, crop, cropRatio },
			editor.imageSize,
			editor.initialOrientation,
		)

		editor.setAdjustments({ crop: newCrop, cropRatio })

		saveCurrentAdjustmentState({
			message: "Crop",
		})
	}

	const handleReset = () => {
		const resetData = resetCropWithConstraint(
			editor.adjustments,
			editor.imageSize,
			editor.initialOrientation,
		)

		editor.setAdjustments(resetData)

		saveCurrentAdjustmentState({
			message: "Reset Crop",
		})
	}

	const straighten = (cropStraighten: number) => {
		const crop = constrainCrop(
			{ ...adjustments, cropStraighten },
			editor.imageSize,
			editor.initialOrientation,
		)
		editor.setAdjustments({ crop, cropStraighten })
	}

	const rotate90 = (direction: number) => {
		const newCrop = handleCropRotate90(editor.adjustments.crop, direction)

		editor.setAdjustments({
			orientation: editor.adjustments.orientation + direction,
			crop: newCrop,
		})

		saveCurrentAdjustmentState({
			message: `Rotate ${direction > 0 ? "CW" : "CCW"}`,
		})
	}

	const flip = (isXAxis = true) => {
		const newCrop = handleCropFlip(editor.adjustments.crop, isXAxis)
		const orientation = editor.orientation

		if ((isXAxis && orientation % 2 === 0) || (!isXAxis && orientation % 2 !== 0)) {
			editor.setAdjustments({
				flipX: !editor.adjustments.flipX,
				cropStraighten: -editor.adjustments.cropStraighten,
				crop: newCrop,
			})
		} else {
			editor.setAdjustments({
				flipY: !editor.adjustments.flipY,
				cropStraighten: -editor.adjustments.cropStraighten,
				crop: newCrop,
			})
		}

		saveCurrentAdjustmentState({
			message: isXAxis ? "Flip Horizontally" : "Flip Vertically",
		})
	}

	const toggleAspectLock = () => {
		setAspectLock(!aspectLock)
	}

	const confirmCrop = () => {
		setIsExpanded(false)
	}

	async function autoStraighten() {
		const imageInfo = currentPhoto ? await getOrExtractImageInfoWithLock(currentPhoto) : null
		const cropStraighten = imageInfo?.features.autoCorrection.cropStraighten

		if (cropStraighten) {
			straighten(cropStraighten)
		}

		saveCurrentAdjustmentState({
			message: "Auto Straighten",
			changedValue: editor.adjustments.cropStraighten.toFixed(2),
		})
	}

	return (
		<div
			aria-label={t("cropPanel.ariaCropPanel")}
			className="sticky top-0 z-10 flex w-full flex-col border-b border-base-900 bg-base-800 p-4 pt-0"
		>
			<div
				aria-disabled={isDisabled}
				className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
			>
				<div className="text-leading my-2 mb-4 flex items-center justify-between text-xs">
					<AspectRatioMenu />

					<Button
						trackingLabel="flip_aspect"
						aria-label={t("cropPanel.ariaFlipCropAspectRatio")}
						variant="icon"
						onClick={handleFlipAspect}
						disableEnterKey
					>
						<Icon name="rotate_90_degrees_ccw" />
					</Button>
					<Button
						trackingLabel="aspect_lock"
						aria-label={t("cropPanel.ariaLockCropAspectRatio")}
						value={String(!aspectLock)}
						variant="icon"
						onClick={toggleAspectLock}
						disableEnterKey
					>
						<Icon name={aspectLock ? "lock" : "lock_open"} />
					</Button>
				</div>

				<Slider
					title={t("cropPanel.sliderStraighten")}
					data-testid="crop_straighten_slider"
					value={adjustments["cropStraighten"]}
					min={-45}
					max={45}
					step={0.01}
					keyboardStep={0.1}
					shiftKeyboardStep={0.5}
					transform={noopSliderTransform}
					displayUnit="°"
					onUpdate={(val) => handleStraighten(val)}
					onUpdateEnded={(val) => handleStraightenEnd(val)}
					onPointerUp={() => showStraightenGrid(false)}
				/>

				<div className="mt-2 flex justify-end gap-2">
					<Button
						trackingLabel="auto_straighten"
						aria-label={t("cropPanel.ariaAutoStraighten")}
						className="w-fit justify-end"
						size="sm"
						onClick={autoStraighten}
						disableEnterKey
					>
						{t("cropPanel.autoStraighten")}
					</Button>
				</div>

				<div className="text-leading my-2 mb-4 text-xs">{t("cropPanel.rotateAndFlip")}</div>

				<div className="flex gap-4 text-xs">
					<Button
						trackingLabel="rotate_left"
						aria-label={t("cropPanel.ariaRotateCropLeft")}
						variant="icon"
						onClick={() => rotate90(-1)}
						disableEnterKey
					>
						<Icon name="rotate_left" />
					</Button>
					<Button
						trackingLabel="rotate_right"
						aria-label={t("cropPanel.ariaRotateCropRight")}
						variant="icon"
						onClick={() => rotate90(1)}
						disableEnterKey
					>
						<Icon name="rotate_right" />
					</Button>
					<Button
						trackingLabel="flip_x"
						aria-label={t("cropPanel.ariaFlipCropHorizontal")}
						variant="icon"
						onClick={() => flip(true)}
						disableEnterKey
					>
						<Icon name="flip_x" />
					</Button>
					<Button
						trackingLabel="flip_y"
						aria-label={t("cropPanel.ariaFlipCropVertical")}
						variant="icon"
						onClick={() => flip(false)}
						disableEnterKey
					>
						<Icon name="flip_y" className="rotate-90" />
					</Button>
				</div>

				<div className="mt-2 flex gap-2">
					<Button
						trackingLabel="reset_crop"
						aria-label={t("cropPanel.ariaResetCrop")}
						className="flex-1"
						size="sm"
						onClick={handleReset}
						disableEnterKey
					>
						{t("common:action.reset")}
					</Button>
					<Button
						trackingLabel="confirm_crop"
						aria-label={t("cropPanel.ariaConfirmCrop")}
						className="flex-1"
						size="sm"
						onClick={confirmCrop}
					>
						{t("common:action.confirm")}
					</Button>
				</div>
			</div>
		</div>
	)
}
